import React, { useMemo } from 'react';
import { 
  format, startOfWeek, endOfWeek, eachDayOfInterval, 
  isToday, isSameMonth, addDays
} from 'date-fns';
import { motion } from 'framer-motion';
import { FaUtensils } from 'react-icons/fa';
import { ChevronRightIcon } from '@heroicons/react/24/solid';

const MealWeekView = ({ mealPlans, selectedDate, onDateClick, isLoading }) => {
  // Get days in current week starting from Monday
  const weekStart = useMemo(() => startOfWeek(selectedDate, { weekStartsOn: 1 }), [selectedDate]);
  const weekEnd = useMemo(() => endOfWeek(selectedDate, { weekStartsOn: 1 }), [selectedDate]);
  
  const daysInWeek = useMemo(() => 
    eachDayOfInterval({ start: weekStart, end: weekEnd }), 
    [weekStart, weekEnd]
  );
  
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-pri-dark">
        <div className="text-center">
          <div className="mb-4 relative w-12 h-12 mx-auto">
            <div className="absolute inset-0 rounded-full border-4 border-t-soft-pink border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
            <div className="absolute inset-2 rounded-full border-4 border-t-transparent border-r-mint-green border-b-transparent border-l-transparent animate-spin"></div>
          </div>
          <p className="text-pri-white">Loading meal plans...</p>
        </div>
      </div>
    );
  }
  
  // Helper to get meals for a specific date
  const getMealsForDate = (date) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    return mealPlans[formattedDate] || {};
  };
  
  // Helper to check if a day has meals
  const dayHasMeals = (date) => {
    const meals = getMealsForDate(date);
    return Object.keys(meals).length > 0;
  };
  
  // Get count of meals for a day
  const getMealCount = (date) => {
    const meals = getMealsForDate(date);
    return Object.keys(meals).length;
  };
  
  // Get a preview of meal names for a day
  const getMealPreview = (date) => {
    const meals = getMealsForDate(date);
    const mealTypes = Object.keys(meals);
    
    if (mealTypes.length === 0) return null;
    
    // Return the first meal name as a preview
    const firstMealType = mealTypes[0];
    const firstMeal = meals[firstMealType];
    
    return firstMeal?.recipe?.name || 'Unnamed meal';
  };
  
  return (
    <div className="h-full overflow-y-auto bg-pri-dark p-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-2xl font-bold text-soft-pink mb-6">
          Meal Plan for Week of {format(weekStart, 'MMMM d')} - {format(weekEnd, 'MMMM d, yyyy')}
        </h2>
        
        <div className="grid grid-cols-1 gap-4">
          {daysInWeek.map((day, index) => {
            const formattedDay = format(day, 'EEEE, MMMM d');
            const isCurrentDay = isToday(day);
            const hasMeals = dayHasMeals(day);
            const mealCount = getMealCount(day);
            const mealPreview = getMealPreview(day);
            const isSameMonthAsSelected = isSameMonth(day, selectedDate);
            
            return (
              <motion.div
                key={index}
                whileHover={{ scale: 1.01, x: 5 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`rounded-xl border ${
                  isCurrentDay 
                    ? 'border-soft-pink' 
                    : 'border-soft-pink/20'
                } ${
                  isSameMonthAsSelected 
                    ? 'bg-gradient-to-br from-pri-dark/80 to-sec-dark/80' 
                    : 'bg-gradient-to-br from-pri-dark/40 to-sec-dark/40'
                } p-4 cursor-pointer`}
                onClick={() => onDateClick(day)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`text-lg font-semibold ${
                      isCurrentDay 
                        ? 'text-soft-pink' 
                        : isSameMonthAsSelected ? 'text-pri-white' : 'text-pri-gray'
                    }`}>
                      {formattedDay}
                    </h3>
                    
                    {hasMeals ? (
                      <div className="mt-2">
                        <div className="flex items-center text-soft-pink/80 text-sm">
                          <FaUtensils className="mr-2 h-3 w-3" />
                          <span>{mealCount} {mealCount === 1 ? 'meal' : 'meals'} planned</span>
                        </div>
                        
                        {mealPreview && (
                          <p className="text-pri-gray mt-1 text-sm line-clamp-1">
                            Including: {mealPreview}
                            {mealCount > 1 ? ' and more...' : ''}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-pri-gray/60 text-sm mt-2">No meals planned</p>
                    )}
                  </div>
                  
                  <ChevronRightIcon className="h-5 w-5 text-soft-pink/50" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MealWeekView; 